import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, UserCog, ShieldCheck, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { PageHeader, EmptyState, LoadingBlock } from "../../components/app/ui";
import { useMyRestaurant } from "./RestaurantOrders";
import RestaurantProfileForm from "../../components/app/RestaurantProfileForm";
import { restaurantApi, errorMessage } from "../../lib/api";

export const PERMISSIONS = [
  { id: "orders", label: "Orders", hint: "Accept and update order status" },
  { id: "menu", label: "Menu", hint: "Add, edit and categorise dishes" },
  { id: "hours", label: "Hours & info", hint: "Online status, hours, photos" },
  { id: "discounts", label: "Discounts", hint: "Create and run promo codes" },
  { id: "reports", label: "Reports", hint: "Analytics and sales reports" },
  { id: "ratings", label: "Ratings & complaints", hint: "View reviews, resolve complaints" },
];
const ALL = PERMISSIONS.map((p) => p.id);
const EMPTY = { name: "", email: "", password: "", permissions: ["orders", "menu"] };

function PermPicker({ value, onToggle, idPrefix }) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid={`${idPrefix}-perms`}>
      {PERMISSIONS.map((p) => {
        const on = value.includes(p.id);
        return (
          <button
            type="button"
            key={p.id}
            onClick={() => onToggle(p.id)}
            className={`flex items-start gap-2 rounded-xl border px-3 py-2.5 text-left transition-colors ${on ? "border-brand bg-brand-50" : "border-surface-300 bg-white hover:border-surface-400"}`}
            data-testid={`${idPrefix}-perm-${p.id}`}
          >
            <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${on ? "border-brand bg-brand text-white" : "border-surface-400"}`}>
              {on && <ShieldCheck className="h-3 w-3" />}
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-ink">{p.label}</span>
              <span className="block text-[11px] text-ink-muted">{p.hint}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function Managers() {
  const [restaurant, setRestaurant] = useMyRestaurant();
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null); // manager being edited
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => restaurantApi.managers().then(setList).catch(() => setList([]));
  useEffect(() => { if (restaurant) load(); }, [restaurant]);

  const toggleCreate = (id) => setForm((f) => ({ ...f, permissions: f.permissions.includes(id) ? f.permissions.filter((x) => x !== id) : [...f.permissions, id] }));
  const toggleEdit = (id) => setEdit((m) => ({ ...m, permissions: m.permissions.includes(id) ? m.permissions.filter((x) => x !== id) : [...m.permissions, id] }));

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) return toast.error("Fill all fields (password 6+ chars)");
    if (form.permissions.length === 0) return toast.error("Pick at least one permission");
    setBusy(true);
    try { await restaurantApi.addManager({ name: form.name.trim(), email: form.email.trim(), password: form.password, permissions: form.permissions }); toast.success("Manager invited"); setForm(EMPTY); setOpen(false); load(); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    if (edit.permissions.length === 0) return toast.error("Pick at least one permission");
    setBusy(true);
    try { await restaurantApi.updateManager(edit.user_id, { permissions: edit.permissions }); toast.success("Permissions updated"); setEdit(null); load(); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name}'s access?`)) return;
    try { await restaurantApi.removeManager(m.user_id); setList((l) => l.filter((x) => x.user_id !== m.user_id)); toast.success("Manager removed"); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  if (restaurant === undefined) return <><PageHeader title="Managers" /><LoadingBlock rows={3} /></>;
  if (restaurant === null) return <><PageHeader title="Set up your restaurant" description="Create your restaurant profile first." /><div className="max-w-3xl"><RestaurantProfileForm onSaved={setRestaurant} title="Create your restaurant" /></div></>;

  return (
    <>
      <PageHeader title="Managers" description="Invite staff and choose exactly what each one can do — e.g. one person only handles orders while another manages the menu." actions={<Button onClick={() => { setForm(EMPTY); setOpen(true); }} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="manager-new"><Plus className="mr-2 h-4 w-4" /> Invite manager</Button>} />
      <div className="mb-6 flex items-start gap-2 rounded-2xl border border-graphite-200 bg-graphite-50 px-5 py-3 text-sm text-ink" data-testid="manager-perms">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-graphite" /> Each manager sees only the sections you grant. Managers can never invite other managers or change ownership.
      </div>
      {list === null && <LoadingBlock rows={3} />}
      {list && list.length === 0 && <EmptyState icon={UserCog} title="No managers yet" text="Invite a manager with the button above to share the workload." />}
      {list && list.length > 0 && (
        <div className="grid gap-3" data-testid="managers-list">
          {list.map((m) => (
            <div key={m.user_id} className="kt-card flex items-start gap-4 p-4" data-testid={`manager-${m.user_id}`}>
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand font-bold">{m.name?.[0]?.toUpperCase()}</span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-ink">{m.name}</p>
                <p className="truncate text-sm text-ink-soft">{m.email}</p>
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`manager-perm-tags-${m.user_id}`}>
                  {(m.permissions || []).map((pid) => (
                    <span key={pid} className="rounded-full bg-surface-200 px-2 py-0.5 text-[11px] font-medium text-ink-soft">{PERMISSIONS.find((p) => p.id === pid)?.label || pid}</span>
                  ))}
                  {(m.permissions || []).length === ALL.length && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">Full access</span>}
                </div>
              </div>
              <button onClick={() => setEdit({ ...m, permissions: [...(m.permissions || [])] })} className="rounded-lg p-2 text-ink-muted hover:bg-surface-200 hover:text-ink" aria-label="Edit permissions" data-testid={`manager-edit-${m.user_id}`}><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(m)} className="rounded-lg p-2 text-ink-muted hover:bg-red-50 hover:text-red-700" aria-label="Remove" data-testid={`manager-remove-${m.user_id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="manager-dialog">
          <DialogHeader><DialogTitle>Invite a manager</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-2"><Label>Full name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="manager-name" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="manager-email" /></div>
            <div className="space-y-2"><Label>Temporary password</Label><Input type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Share this with them" data-testid="manager-password" /></div>
            <div className="space-y-2"><Label>Permissions</Label><PermPicker value={form.permissions} onToggle={toggleCreate} idPrefix="create" /></div>
            <DialogFooter><Button type="submit" disabled={busy} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="manager-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create manager</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="sm:max-w-md" data-testid="manager-edit-dialog">
          <DialogHeader><DialogTitle>{edit?.name}'s permissions</DialogTitle></DialogHeader>
          {edit && (
            <form onSubmit={saveEdit} className="space-y-4">
              <PermPicker value={edit.permissions} onToggle={toggleEdit} idPrefix="edit" />
              <DialogFooter><Button type="submit" disabled={busy} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="manager-edit-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save permissions</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
